import React, {
  memo,
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react'
import { Edit, MapPin, Trash2 as IconThrash, Link } from 'react-feather'
import { useIntl, FormattedMessage } from 'react-intl'
import {
  Button,
  Card,
  Collapse,
  FormGroup,
  Spinner,
  UncontrolledTooltip,
} from 'reactstrap'
import styled from 'styled-components'
import { shade } from 'polished'
import _ from 'lodash'
import useAxios from 'axios-hooks'

import Avatar from '~/components/Avatar'
import CheckBox from '~/components/Checkbox'
import Textarea from '~/components/Textarea'
import { Hide, Iterate, Show, TextBox } from '~/components/commons'
import { queries } from '~/constants/mediaQuery'
import { getOriginalComment } from '~/utility/gmb/comment'
import { formatISO } from '~/utility/date'
import { PageContext } from '.'
import { VariablesSelect as Select } from '../TextTemplates'
import { toast } from 'react-toastify'

import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Alert } from '~/components/Alert'
import { useSelector } from 'react-redux'

const usePostReplyAxios = questionName => {
  const questionId = React.useMemo(() => questionName.split('/').pop(), [
    questionName,
  ])

  return useAxios(
    {
      method: 'PUT',
      url: `/questions/${encodeURIComponent(questionId)}/reply`,
    },
    { manual: true }
  )
}

function ReplyForm({
  id,
  setNotFoundModal,
  questionName,
  focusId,
  setFocusId,
  onReply,
  templates,
}) {
  const intl = useIntl()
  const [input, setInput] = useState('')
  const [focused, setFocused] = useState(false)
  const [{ loading }, postReply] = usePostReplyAxios(questionName)
  const inputRef = useRef()
  const variableOptions = templates.map(t => ({
    label: t.name,
    value: t.content,
  }))

  if (!setFocusId) {
    setFocusId = setFocused
    focusId = focused
  }

  async function handleSubmit() {
    try {
      const { data } = await postReply({ data: { text: input } })
      onReply(data?.answer)
      setInput('')
    } catch (err) {
      if (err?.response?.status === 404) return setNotFoundModal(true)
      toast.error(
        intl.formatMessage({
          id: (
            err?.response?.data?.error ?? 'Error while answering question.'
          ).trim(),
        })
      )
      console.error(err?.response?.data?.error)
    }
  }

  useEffect(() => {
    if (focusId && focusId !== id) setFocused(false)
    if (!focused && focusId && focusId === id) {
      inputRef.current.focus()
      setFocused(true)
    }
    // eslint-disable-next-line
  }, [focusId, id])

  return (
    <ReplyForm.Container>
      <FormGroup className="position-relative form-label-group m-0 w-100">
        <ReplyForm.Input
          ref={inputRef}
          onFocus={() => {
            if (!focused) setFocusId(id)
          }}
          value={input}
          resize="vertical"
          onChange={e => setInput(e.target.value)}
          setValue={e => setInput(e)}
          enableVariables={focused}
          disabled={loading}
          placeholder={intl.formatMessage({ id: 'Write your reply' })}
          style={{
            height: focused ? '96px' : '38px',
            minHeight: focused ? '96px' : '38px',
            resize: focused ? 'vertical' : 'none',
            overflow: focused ? 'auto' : 'hidden',
          }}
        />
        <Show condition={loading}>
          <div className="form-control-position">
            <Spinner size="sm" color="light" />
          </div>
        </Show>
      </FormGroup>
      <Show condition={focused}>
        <ReplyForm.Buttons>
          <Select
            options={variableOptions}
            style={{ minWidth: '200px !important' }}
            className="mr-25"
            onChange={({ value }) => setInput(value)}
            placeholder="Templates"
            noOptionsMessage={() => (
              <Link to="/templates/questions">
                <FormattedMessage id="No template have been created yet. Click and create one." />
              </Link>
            )}
          />
          <Button
            color="primary"
            disabled={!input.length || loading}
            onClick={handleSubmit}
            style={{ fontWeight: '600' }}
          >
            <FormattedMessage id="Post Reply" />
          </Button>
        </ReplyForm.Buttons>
      </Show>
    </ReplyForm.Container>
  )
}

function EditReplyForm({
  setNotFoundModal,
  questionName,
  onSave,
  initialValue,
  templates,
}) {
  const intl = useIntl()
  const [input, setInput] = useState(initialValue)
  const [{ loading }, postReply] = usePostReplyAxios(questionName)
  const variableOptions = templates.map(t => ({
    label: t.name,
    value: t.content,
  }))

  async function handleSubmit() {
    try {
      if (input !== initialValue) {
        const { data } = await postReply({ data: { text: input } })
        return onSave(data?.answer)
      }

      onSave()
    } catch (err) {
      if (err?.response?.status === 404) return setNotFoundModal(true)
      toast.error(
        intl.formatMessage({
          id: (
            err?.response?.data?.error ?? 'Error while answering question.'
          ).trim(),
        })
      )
      console.error(err?.response?.data?.error)
    }
  }

  return (
    <ReplyForm.Container style={{ marginTop: '16px' }}>
      <FormGroup className="position-relative form-label-group m-0 w-100">
        <Textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          setValue={e => setInput(e)}
          enableVariables
          disabled={loading}
          placeholder={useIntl().formatMessage({ id: 'Write your reply' })}
          style={{ height: 96 }}
          className="border-info"
        />
        <Show condition={loading}>
          <div className="form-control-position">
            <Spinner size="sm" color="light" />
          </div>
        </Show>
      </FormGroup>
      <ReplyForm.ActionsContainer>
        <ReplyForm.Buttons>
          <Select
            options={variableOptions}
            style={{ minWidth: '200px !important' }}
            className="mr-25"
            onChange={({ value }) => setInput(value)}
            placeholder="Templates"
            noOptionsMessage={() => (
              <Link to="/templates/questions">
                <FormattedMessage id="No template have been created yet. Click and create one." />
              </Link>
            )}
          />
          <EditReplyForm.ReplyButton
            disabled={!input.length || loading}
            onClick={handleSubmit}
          >
            <FormattedMessage id="Edit Reply" />
          </EditReplyForm.ReplyButton>
        </ReplyForm.Buttons>
      </ReplyForm.ActionsContainer>
    </ReplyForm.Container>
  )
}

function QuestionAnswer({
  item: { text, createTime, name, author },
  id,
  setNotFoundModal,
  originalComment,
  locationId,
  onEditReply,
  questionName,
  templates,
}) {
  const [fullText, setFullText] = useState(text)
  const [isEditingReply, setIsEditingReply] = useState(false)

  const originalText = useMemo(() => getOriginalComment(fullText), [fullText])
  const isFromOwner = useMemo(
    () => ['MERCHANT', 'TEMPORARY'].includes(author?.type),
    [author]
  )

  const onReply = useCallback(
    reply => {
      setIsEditingReply(false)
      if (reply) {
        onEditReply(name, reply)
        setFullText(reply?.text)
      }
    },
    [name, onEditReply]
  )

  return (
    <>
      <div
        className="mt-1"
        style={{
          border: '1px solid #E2E8F0',
          borderRadius: '5px',
          padding: '0.8rem',
        }}
      >
        <div className="d-flex align-items-center">
          <div>
            <Avatar
              className="mr-1"
              bgColor="#EBE9FD"
              img={author?.profilePhotoUri}
              icon
            />
          </div>
          <div style={{ flex: 1 }}>
            <QuestionCard.ReviewerName style={{ fontSize: 12 }}>
              {author?.type === 'TEMPORARY' ? (
                <FormattedMessage id="Author (Updating on Google...)" />
              ) : (
                author?.displayName
              )}{' '}
              <small className="text-secondary font-small-2">
                {formatISO(createTime)}
              </small>
            </QuestionCard.ReviewerName>
          </div>
          <div className="pl-2">
            <Hide condition={!isFromOwner}>
              <div
                className="px-2"
                style={{
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Button
                  style={{ padding: '10px', background: '#F7F7F7' }}
                  className="rounded border-0"
                  outline
                  color="secondary"
                  onClick={() => setIsEditingReply(prevState => !prevState)}
                >
                  <Edit size={15} color="#7367F0" strokeWidth={2} />
                </Button>
              </div>
            </Hide>
          </div>
        </div>

        <Hide condition={isEditingReply}>
          <TextBox fontSize={12} style={{ marginLeft: 'calc(45px + 1rem)' }}>
            {originalText}
          </TextBox>
        </Hide>

        <Show condition={isEditingReply}>
          <EditReplyForm
            id={id}
            setNotFoundModal={setNotFoundModal}
            locationId={locationId}
            comment={originalComment}
            questionName={questionName}
            onSave={onReply}
            initialValue={originalText}
            templates={templates}
          />
        </Show>
      </div>
    </>
  )
}

function QuestionCard({
  item: {
    questionData: {
      author,
      text,
      answers: topAnswers = [],
      createTime,
      name: questionName,
      isOwner,
      id,
    } = {},
    locationData: location,
  },
  setNotFoundModal,
  focusId,
  setFocusId,
  excluded = [],
  onChangeChecked,
  templates,
  onDeletedQuestion = () => {},
  clipboardLink,
}) {
  const [alertVisible, setAlertVisible] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [answers, setAnswers] = useState(topAnswers)
  const { isBulkReplying: selectable } = useContext(PageContext)
  const questionId = questionName.split('/').slice(-1)[0]
  const projectId = useSelector(state => state.user?.selectedProject?.id)

  const checked = useMemo(() => !excluded.includes(questionName), [
    excluded,
    questionName,
  ])

  const toastMessage = useIntl().formatMessage({ id: 'Copied to Clipboard' })
  const originalComment = useMemo(() => getOriginalComment(text), [text])
  const hasOwnerAnswer = useMemo(
    () =>
      answers?.some(answer =>
        ['MERCHANT', 'TEMPORARY'].includes(answer?.author?.type)
      ),
    [answers]
  )

  const [, deleteQuestion] = useAxios(
    {
      method: 'DELETE',
      url: `/projects/${projectId}/answered-question`,
      data: {
        questionId: questionName,
      },
    },
    { manual: true }
  )

  const handleQuestionDelete = () => {
    deleteQuestion()
      .then(({ data }) => {
        setModalMessage(data.message)
        setAlertVisible(true)
        onDeletedQuestion(questionName)
      })
      .catch(({ data }) => {
        toast.error(data.message)
      })
  }

  return (
    <QuestionCard.Container selected={selectable && checked}>
      <QuestionCard.Heading>
        <div>
          <Avatar size="lg" img={author?.profilePhotoUri} />
        </div>
        <div style={{ flex: 2 }}>
          <QuestionCard.ReviewerName>
            {author?.displayName}{' '}
            <small
              className="text-secondary font-small-2"
              style={{ marginLeft: 5 }}
            >
              {formatISO(createTime)}
            </small>
          </QuestionCard.ReviewerName>
        </div>
        <Show condition={isOwner && !selectable}>
          <div
            className="px-2"
            style={{
              height: '50px',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Button
              style={{ padding: '10px', background: '#F7F7F7' }}
              className="rounded-circle border-0"
              outline
              color="secondary"
              onClick={() => handleQuestionDelete()}
            >
              <IconThrash size={14} className="text-danger" />
            </Button>
          </div>
        </Show>
        <QuestionCard.Location>
          <MapPin size={24} />
          <div className="d-flex align-items-center">
            <p style={{ lineHeight: '0.9rem' }}>
              <strong>{location?.title}</strong> -{' '}
              {location?.storefrontAddress?.addressLines?.join(' ')},&nbsp;
              {location?.storefrontAddress?.locality}&nbsp;-&nbsp;
              {location?.storefrontAddress?.administrativeArea}&nbsp;
              {location?.storeCode && `(${location?.storeCode})`}
            </p>
          </div>
        </QuestionCard.Location>
        <CopyToClipboard
          text={
            clipboardLink ||
            `${window.location.origin.toString()}/locations/question/${encodeURIComponent(
              questionId
            )}`
          }
          onCopy={() => toast.success(toastMessage)}
          title="Copy question link"
        >
          <QuestionCard.CopyButton id={`copy-link-${id.replaceAll('%', '')}`}>
            <Link color="#C4C4C4" size={20} strokeWidth={3} />
            <UncontrolledTooltip
              placement="left"
              target={`copy-link-${id.replaceAll('%', '')}`}
            >
              <FormattedMessage id="Copy Link" />
            </UncontrolledTooltip>
          </QuestionCard.CopyButton>
        </CopyToClipboard>
      </QuestionCard.Heading>

      <QuestionCard.Body selectable={selectable}>
        <Show condition={selectable}>
          <QuestionCard.CheckboxContainer>
            <div>
              <CheckBox
                type="checkbox"
                checked={checked}
                onChange={e => onChangeChecked(e.target.checked, questionName)}
                className="justify-content-center"
              />
            </div>
          </QuestionCard.CheckboxContainer>
        </Show>

        <TextBox className="w-100 align-self-center">{originalComment}</TextBox>

        <Show condition={!!answers}>
          <Iterate
            data={answers}
            render={QuestionAnswer}
            setNotFoundModal={setNotFoundModal}
            questionName={questionName}
            templates={templates}
            id={id}
            locationId={location?.name}
            locationName={location?.title}
            originalComment={originalComment}
            onEditReply={(name, newReply) => {
              const idx = _.findIndex(answers, { name })
              setAnswers(prev => {
                prev.splice(idx, 1, newReply)
                return prev
              })
            }}
          />
        </Show>

        <Collapse isOpen={!hasOwnerAnswer && !selectable}>
          <ReplyForm
            id={id}
            setNotFoundModal={setNotFoundModal}
            locationId={location?.name}
            originalComment={originalComment}
            focusId={focusId}
            setFocusId={setFocusId}
            questionName={questionName}
            templates={templates}
            onReply={answer => {
              setAnswers(prev => [...prev, answer])
            }}
          />
        </Collapse>
      </QuestionCard.Body>
      <Alert
        type="success"
        visible={alertVisible}
        onClose={() => setAlertVisible(false)}
        confirmBtnText="Close"
        body={modalMessage}
        title="Success"
      />
    </QuestionCard.Container>
  )
}

QuestionCard.Container = styled(Card)`
  padding: 20px;
  transition: all 0.1s;
  box-shadow: ${props =>
    props.selected ? `0 0 0 3px ${props.theme.color.primary}` : ''};
`

QuestionCard.Heading = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

QuestionCard.ReviewerName = styled.strong`
  font-size: 16px;
`

QuestionCard.Body = styled.div`
  display: ${props => (props.selectable ? `flex` : '')};

  p {
    margin-top: 10px;
  }

  @media ${queries.more.md} {
    padding-left: ${props => (props.selectable ? '' : '70px')};

    p {
      margin-top: 0;
    }
  }
`

QuestionCard.CheckboxContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 70px;
  margin-top: 5px;
`

QuestionCard.Location = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  color: #aaa;
  font-size: 12px;

  p {
    margin: 0;
    font-size: 12px;
  }
`

QuestionCard.CopyButton = styled.button`
  width: 2.75rem;
  height: 2.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  border: 1px solid #f7f7f7;
  background-color: #f7f7f7;

  &:hover {
    background-color: ${shade(0.1, '#f7f7f7')};
    transition: 0.2s;
  }
`

ReplyForm.Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  margin-top: 5px;

  button {
    padding: 12px 11px;
    min-width: fit-content;
  }
`

ReplyForm.Input = styled(Textarea)``

ReplyForm.ActionsContainer = styled.div`
  display: flex;
  gap: 5px;

  button {
    min-width: max-content;
    width: 100%;
  }
`

ReplyForm.CancelButton = styled(Button)`
  background-color: #8d8d8d !important;
  color: #fff;
`

ReplyForm.ReplyButton = styled(Button)`
  background-color: #0091ff !important;
  color: #fff;
`

EditReplyForm.ActionsContainer = styled.div`
  display: flex;
  gap: 5px;

  button {
    min-width: max-content;
    width: 100%;
  }
`

EditReplyForm.CancelButton = styled(Button)`
  background-color: #8d8d8d !important;
  color: #fff;
`

EditReplyForm.ReplyButton = styled(Button)`
  background-color: #7367f0 !important;
  color: #fff;
  font-weight: 600;
`

QuestionCard.ReviewerName = styled.strong`
  font-size: 16px;
`

ReplyForm.Buttons = styled.div`
  display: flex;
  gap: 5px;
  margin-top: 5px;
`

export default memo(QuestionCard)
